/**
 * QUIPWA — DeckSync
 *
 * Keeps each deck's local `collection.db` (card content) in step with its
 * `.wflash` file in Drive. Runs after the state and outbox phases, so every
 * deck another device created is already known through the replicated
 * `_state.db`, and this device's own pending uploads have been sent.
 *
 * For every `.wflash` in the QUIPWA folder (one listing call):
 *
 *  - known deck with an upload still queued → skip (this device's edits go up
 *                                              first; see DeckUploads)
 *  - known deck, no local content            → download it (hydrate)
 *  - known deck, Drive `version` differs     → download it and reconcile card
 *    from the one this device last applied     ids (FSRS state of surviving
 *                                              cards is kept)
 *  - unknown file, not deleted               → import it as a new deck
 *  - file of a deck the user deleted (`deck_file` tombstone) → ignore it
 *
 * "Last applied" is a device-local bookmark (`KV.deckVersion`). It used to be
 * the replicated `drive_modified_at`, which the uploading device updates too —
 * so every other device concluded it was already up to date and never
 * downloaded the new cards.
 */

import type { DriveService, DriveFile } from '../drive/drive-service.ts'
import type { StateDBService } from '../db/state-db-service.ts'
import type { WFlashImporter } from '../wflash/wflash-importer.ts'
import type { CollectionDBService } from '../db/collection-db-service.ts'
import type { KvStore } from '../local/kv-store.ts'
import { KV } from '../local/kv-store.ts'

export interface DeckSyncReport {
  hydrated: number
  updated: number
  imported: number
  failed: Array<{ name: string; error: string }>
}

export interface DeckSyncDeps {
  drive: Pick<DriveService, 'listWFlashFiles' | 'downloadFile'>
  state: StateDBService
  importer: WFlashImporter
  kv: KvStore
  getCollection: (deckInstanceId: string) => Promise<CollectionDBService>
  /** True while a local change to the deck waits in the outbox. */
  hasPendingUpload?: (deckInstanceId: string) => Promise<boolean>
}

export class DeckSync {
  private readonly deps: DeckSyncDeps

  constructor(deps: DeckSyncDeps) {
    this.deps = deps
  }

  async run(): Promise<DeckSyncReport> {
    const report: DeckSyncReport = { hydrated: 0, updated: 0, imported: 0, failed: [] }
    const files = await this.deps.drive.listWFlashFiles()
    const decks = await this.deps.state.getDeckInstances()
    const deleted = await this.deps.state.getDeletedDeckFileIds()
    const byFileId = new Map(decks.filter((d) => d.drive_file_id).map((d) => [d.drive_file_id, d]))

    for (const file of files) {
      if (deleted.has(file.id)) continue
      const deck = byFileId.get(file.id)
      try {
        if (!deck) {
          await this.importNew(file)
          report.imported += 1
          continue
        }
        if (await this.deps.hasPendingUpload?.(deck.id)) continue
        const collection = await this.deps.getCollection(deck.id)
        const empty = (await collection.getCardCount()) === 0
        const applied = await this.deps.kv.get<string>(KV.deckVersion(deck.id))
        if (empty || applied !== (file.version ?? null)) {
          await this.refresh(deck.id, file)
          if (empty) report.hydrated += 1
          else report.updated += 1
        }
      } catch (err) {
        report.failed.push({ name: file.name, error: err instanceof Error ? err.message : String(err) })
      }
    }
    return report
  }

  /** Download a deck's file and make the local copy match it. */
  async refresh(deckInstanceId: string, file: DriveFile): Promise<void> {
    const buffer = await this.deps.drive.downloadFile(file.id)
    const collection = await this.deps.getCollection(deckInstanceId)
    await collection.replaceFromWflash(buffer)
    // The importer recognises the deck by its drive_file_id and reconciles card
    // ids: new cards start fresh, removed ones are tombstoned, the rest keep
    // their FSRS state.
    await this.deps.importer.importFromArrayBuffer(buffer, file, async () => 'update')
    await this.markApplied(deckInstanceId, file)
  }

  private async importNew(file: DriveFile): Promise<void> {
    const buffer = await this.deps.drive.downloadFile(file.id)
    const result = await this.deps.importer.importFromArrayBuffer(buffer, file)
    if (result.errors.length > 0) throw new Error(result.errors.join('; '))
    const collection = await this.deps.getCollection(result.deck_instance_id)
    await collection.replaceFromWflash(buffer)
    await this.markApplied(result.deck_instance_id, file)
  }

  private async markApplied(deckInstanceId: string, file: DriveFile): Promise<void> {
    if (file.version) await this.deps.kv.set(KV.deckVersion(deckInstanceId), file.version)
  }
}
